import React, { useRef, useState, useEffect, useCallback } from 'react';

const DualThumbSlider = ({ value, onChange, min = 1, max = 25 }) => {
  const [lower, setLower] = useState(value[0]);
  const [upper, setUpper] = useState(value[1]);
  const sliderRef = useRef(null);

  // Update state if external value changes
  useEffect(() => {
    setLower(value[0]);
    setUpper(value[1]);
  }, [value]);

  // Convert a value to a percentage for positioning
  const percentFromValue = useCallback(
    (val) => ((val - min) / (max - min)) * 100,
    [min, max]
  );

  // Common handler for thumb dragging
  const handleMouseMove = useCallback(
    (thumb, clientX) => {
      if (!sliderRef.current) return;
      const rect = sliderRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const percent = Math.min(Math.max(x / rect.width, 0), 1);
      const newValue = Math.round(min + percent * (max - min));

      if (thumb === 'lower') {
        const clamped = Math.min(newValue, upper - 1);
        setLower(clamped);
        onChange([clamped, upper]);
      } else if (thumb === 'upper') {
        const clamped = Math.max(newValue, lower + 1);
        setUpper(clamped);
        onChange([lower, clamped]);
      }
    },
    [min, max, lower, upper, onChange]
  );

  // Start dragging a thumb
  const handleMouseDown = (thumb, e) => {
    e.preventDefault();
    const moveHandler = (moveEvent) => {
      handleMouseMove(thumb, moveEvent.clientX);
    };
    const upHandler = () => {
      document.removeEventListener('mousemove', moveHandler);
      document.removeEventListener('mouseup', upHandler);
    };
    document.addEventListener('mousemove', moveHandler);
    document.addEventListener('mouseup', upHandler);
  };

  const lowerPercent = percentFromValue(lower);
  const upperPercent = percentFromValue(upper);

  return (
    <div
      ref={sliderRef}
      style={{
        position: 'relative',
        height: '6px',
        margin: '30px 0',
      }}
    >
      {/* Left segment: Green */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          width: `${lowerPercent}%`,
          height: '100%',
          background: 'var(--green-risk-score)',
        }}
      />
      {/* Middle segment: Yellow */}
      <div
        style={{
          position: 'absolute',
          left: `${lowerPercent}%`,
          width: `${upperPercent - lowerPercent}%`,
          height: '100%',
          background: 'var(--yellow-risk-score)',
        }}
      />
      {/* Right segment: Red */}
      <div
        style={{
          position: 'absolute',
          left: `${upperPercent}%`,
          width: `${100 - upperPercent}%`,
          height: '100%',
          background: 'var(--red-risk-score)',
        }}
      />

      {/* Lower thumb */}
      <div
        onMouseDown={(e) => handleMouseDown('lower', e)}
        style={{
          position: 'absolute',
          top: '-8px',
          left: `calc(${lowerPercent}% - 7px)`,
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: '#fff',
          border: '2px solid #777',
          cursor: 'pointer',
          zIndex: 2,
        }}
      >
        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px' }}>{lower}</div>
      </div>

      {/* Upper thumb */}
      <div
        onMouseDown={(e) => handleMouseDown('upper', e)}
        style={{
          position: 'absolute',
          top: '-8px',
          left: `calc(${upperPercent}% - 7px)`,
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: '#fff',
          border: '2px solid #777',
          cursor: 'pointer',
          zIndex: 2,
        }}
      >
        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px' }}>{upper}</div>
      </div>
    </div>
  );
};

export default DualThumbSlider;
